import type { Reading } from "@spf/contracts";
import { Card } from "../Card.js";
import { FlowStage } from "../flow/FlowStage.js";
import { FlowLegend } from "../flow/FlowLegend.js";
import { toFlowModel } from "../../lib/flow.js";
import { cn } from "../../lib/cn.js";

/**
 * The design's Energy Flow panel.
 *
 * The stage is the design's geometry; what travels it comes from
 * `toFlowModel`, unchanged and reused rather than reimplemented. That module
 * holds the sign conventions -- exactly zero is idle rather than "0 W
 * discharging", and an off-grid inverter never exports so a non-positive grid
 * figure is "not carrying anything" rather than a reverse flow -- and every
 * one of them is already pinned by tests. The design ships hardcoded flow
 * figures and a mode switcher that drives them; taking those would have
 * re-derived by hand, and got wrong, rules that were already verified.
 *
 * The design's `solar-charge` / `grid-export` / `discharge` switcher is gone
 * with them. It sets which animation plays, which on real telemetry is not a
 * choice: what is moving is whatever the inverter reports.
 */
export function EnergyFlowWidget({
  reading,
  stale,
  onExpand,
  className,
}: {
  reading: Reading;
  stale: boolean;
  onExpand?: () => void;
  className?: string;
}) {
  const model = toFlowModel(reading);

  return (
    <Card
      onExpand={onExpand}
      title="Energy Flow"
      headerRight={
        <span
          className={cn(
            "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium",
            stale
              ? "border-danger/30 bg-danger/10 text-danger-ink"
              : "border-accent-2/20 bg-accent-2/10 text-accent-2-ink",
          )}
        >
          <span
            aria-hidden="true"
            className={cn("h-1.5 w-1.5 rounded-full", stale ? "bg-danger" : "bg-accent-2")}
          />
          {/* The word, not only the dot. */}
          <span>{stale ? "Stale" : "Live"}</span>
        </span>
      }
      className={cn("col-span-1 flex flex-col justify-between md:col-span-2", className)}
    >
      <div data-stale={stale} className={stale ? "opacity-60" : undefined}>
        <FlowStage model={model} stale={stale} />
        <FlowLegend flows={model.flows} />
      </div>
    </Card>
  );
}
